import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { backendErrorNotification } from '../../../../components/Notification';
import {
  ActionButtonsContainer,
  DeleteIconTable,
  EditIconTable,
} from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { visitMotiveAPI } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { VisitMotive } from '../../../../types';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';
import checkPermission from '../../../../utils/checkPermission';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import ModalCreate from './Components/ModalCreate';
import ModalDelete from './Components/ModalDelete';
import ModalEdit from './Components/ModalEdit';

const VisitMotivesPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [visitMotives, setVisitMotives] = useState<VisitMotive[] | []>([]);
  const [selectedItem, setSelectedItem] = useState<VisitMotive | null>(null);

  const [showModalCreate, setShowModalCreate] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const [updateTable, setUpdateTable] = useState(0);

  const getVisitMotive: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = visitMotiveAPI.axios.CancelToken.source();

    try {
      // eslint-disable-next-line max-len
      visitMotiveAPI.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await visitMotiveAPI.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<VisitMotive[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!visitMotiveAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const handleTableDeleteBtn = (row: VisitMotive) => {
    setSelectedItem(row);
    setShowModalDelete(true);
  };

  const handleTableEditBtn = (row: VisitMotive) => {
    setSelectedItem(row);
    setShowModalEdit(true);
  };

  const actionsButtons = (row: VisitMotive) => (
    <ActionButtonsContainer>
      {checkPermission('motiveVisit-edit') && (
        <EditIconTable title={t('edit')} onClick={() => handleTableEditBtn(row)} />
      )}
      <DeleteIconTable onClick={() => handleTableDeleteBtn(row)} />
    </ActionButtonsContainer>
  );

  const handleActiveColumn = (row: VisitMotive) => (
    <p
      style={{
        color: row.ATIVO.toLowerCase() === 's' ? theme.colors.positive : theme.colors.negative,
      }}
    >
      {row.ATIVO.toLowerCase() === 's' ? t('active') : t('inactive')}
    </p>
  );

  const columns: Column<VisitMotive>[] = [
    {
      label: t('id') as string,
      accessor: 'CodigoMotivo',
    },
    {
      label: t('scheduleType') as string,
      accessor: 'FlagMotivo',
    },
    {
      label: t('description') as string,
      accessor: 'Descricao',
    },
    {
      label: t('active') as string,
      accessor: 'ATIVO',
      Cell: handleActiveColumn,
    },
    {
      label: '',
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('motivesSchedule')}>
      <NewRegisterBtn
        onClick={() => setShowModalCreate(true)}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      <AsyncTable
        tableName={t('motivesSchedule')}
        columns={columns}
        value={visitMotives}
        onChange={setVisitMotives}
        requestPage={getVisitMotive}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />

      {showModalCreate && (
        <ModalCreate
          closeModal={() => setShowModalCreate(false)}
          reqCallback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      {showModalEdit && selectedItem && (
        <ModalEdit
          selectedItem={selectedItem}
          closeModal={() => setShowModalEdit(false)}
          reqCallback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      {showModalDelete && (
        <ModalDelete
          selectedItem={selectedItem as VisitMotive}
          closeModal={() => setShowModalDelete(false)}
          reqCallback={() => setUpdateTable(updateTable + 1)}
        />
      )}
    </ContentContainer>
  );
};

export default VisitMotivesPage;
